import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Package,
  Search,
  Filter,
  Star,
  ShoppingCart,
  ArrowLeft,
  Store,
  DollarSign,
  Grid,
  List,
  ChevronRight,
  TrendingUp
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import marketplaceAPI from '../../services/marketplace.api';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import OrderModal from '../../components/community/OrderModal';
import { PRODUCT_SERVICE_CATEGORIES } from '../../config/categories';

const ProductListPage = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Order modal state
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  const { selectedBusiness, getBusinessDetails } = useMarketplaceStore();

  useEffect(() => {
    loadData();
  }, [businessId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      await getBusinessDetails(businessId);
      await loadProducts();
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load products');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await marketplaceAPI.getProducts(businessId, { status: 'ACTIVE' });
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to load products:', error);
    }
  };

  // Get unique categories from products
  const productCategories = ['all', ...new Set(products.map(p => p.category).filter(Boolean))];

  const getCategoryIcon = (categoryId) => {
    const category = PRODUCT_SERVICE_CATEGORIES.find(c => c.id === categoryId);
    return category?.icon || '📦';
  };

  const getCategoryName = (categoryId) => {
    const category = PRODUCT_SERVICE_CATEGORIES.find(c => c.id === categoryId);
    return category?.name || categoryId;
  };

  const getSortedProducts = () => {
    let sorted = [...products];
    
    switch (sortBy) {
      case 'price_low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price_high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      case 'featured':
      default:
        return sorted.sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));
    }
  };

  const filteredProducts = getSortedProducts().filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 ${
              star <= Math.floor(rating)
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleOrder = (product) => {
    if (!user) {
      toast.error('Please login to place an order');
      return;
    }
    setSelectedProduct(product);
    setShowOrderModal(true);
  };

  // Render category filter UI
  const renderCategoryFilter = () => {
    if (productCategories.length <= 1) return null;

    return (
      <div className="flex overflow-x-auto hide-scrollbar space-x-2 pb-2">
        {productCategories.map(cat => (
          <button
            key={cat}
            onClick={() => setSelectedCategory(cat)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              selectedCategory === cat
                ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30'
                : 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover'
            }`}
          >
            {cat === 'all' ? (
              'All Products'
            ) : (
              <span className="flex items-center gap-1">
                <span>{getCategoryIcon(cat)}</span>
                {getCategoryName(cat)}
              </span>
            )}
          </button>
        ))}
      </div>
    );
  };

  // Stats for header
  const stats = {
    total: products.length,
    inStock: products.filter(p => p.quantity > 0).length,
    featured: products.filter(p => p.featured).length
  };

  return (
    <div className="min-h-screen bg-[#E8ECF5] pb-16 sm:pb-0">
      {/* Header */}
      <div className="bg-[#E8ECF5] border-b border-primary-100/50 shadow-neomorph sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4 md:py-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center min-w-0">
              <button
                onClick={() => navigate(-1)}
                className="mr-2 sm:mr-3 md:mr-4 p-1.5 sm:p-2 rounded-lg bg-white shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
              >
                <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
              </button>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-0.5 sm:mb-1">
                  <div className="w-1 h-4 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-3" />
                  <button
                    onClick={() => navigate(`/community/marketplace/business/${businessId}`)}
                    className="text-[10px] sm:text-xs md:text-sm text-gray-500 hover:text-primary-600 transition-colors flex items-center truncate"
                  >
                    <Store className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                    <span className="truncate max-w-[100px] sm:max-w-none">{selectedBusiness?.name}</span>
                    <ChevronRight className="h-2.5 w-2.5 sm:h-3 sm:w-3 ml-0.5 sm:ml-1 text-gray-400 flex-shrink-0" />
                  </button>
                  <span className="text-xs sm:text-sm text-gray-300 mx-0.5 sm:mx-2">/</span>
                  <h1 className="text-sm sm:text-base md:text-lg font-bold text-gray-800 truncate">Products</h1>
                </div>
                <p className="text-[9px] sm:text-[11px] md:text-xs text-gray-500 ml-2 sm:ml-4">
                  {filteredProducts.length} of {products.length} products available
                </p>
              </div>
            </div>

            {/* Quick Stats - Hidden on very small screens */}
            <div className="hidden xs:flex items-center space-x-2 sm:space-x-3">
              <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
                <p className="text-[9px] sm:text-xs text-gray-500">In Stock</p>
                <p className="text-[11px] sm:text-sm font-semibold text-green-600">{stats.inStock}</p>
              </div>
              <div className="bg-white rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 shadow-neomorph-inset">
                <p className="text-[9px] sm:text-xs text-gray-500">Featured</p>
                <p className="text-[11px] sm:text-sm font-semibold text-yellow-600">{stats.featured}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-3 sm:px-4 md:px-6 lg:px-8">
        {/* Filters Bar */}
        <div className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 mb-4 sm:mb-6">
          <div className="flex flex-col space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 sm:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="block w-full pl-8 sm:pl-9 pr-2.5 sm:pr-3 py-1.5 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                />
              </div>
              
              <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                <div className="flex items-center space-x-1.5 sm:space-x-2 flex-1 sm:flex-none">
                  <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400 flex-shrink-0" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="flex-1 sm:flex-none px-2 sm:px-3 py-1.5 sm:py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                  >
                    <option value="featured">Featured</option>
                    <option value="newest">Newest</option>
                    <option value="price_low">Price: Low to High</option>
                    <option value="price_high">Price: High to Low</option>
                  </select>
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                      viewMode === 'grid' 
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30' 
                        : 'bg-white text-gray-600 shadow-neomorph hover:shadow-neomorph-hover'
                    }`}
                  >
                    <Grid className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 sm:p-2 rounded-lg transition-all ${
                      viewMode === 'list' 
                        ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-md shadow-primary-500/30' 
                        : 'bg-white text-gray-600 shadow-neomorph hover:shadow-neomorph-hover'
                    }`}
                  >
                    <List className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Category Filter */}
            {renderCategoryFilter()}
          </div>
        </div>

        {/* Products - 2 columns on mobile, 3 on tablet, 4 on desktop for grid view */}
        {isLoading ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-3 sm:gap-5`}>
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 animate-pulse">
                <div className="h-32 sm:h-48 bg-gray-200 rounded-lg mb-3 sm:mb-4 shadow-neomorph-inset"></div>
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 mb-1.5 sm:mb-2"></div>
                <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredProducts.length > 0 ? (
          <div className={`grid ${viewMode === 'grid' ? 'grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'} gap-3 sm:gap-5`}>
            {filteredProducts.map(product => (
              <div key={product.id} className="bg-white rounded-xl p-3 sm:p-4 shadow-neomorph border border-gray-100 hover:shadow-neomorph-hover transition-all">
                {/* Product Image */}
                {product.images?.[0] ? (
                  <div className="w-full h-28 sm:h-48 bg-[#E8ECF5] rounded-lg mb-2 sm:mb-4 shadow-neomorph-inset overflow-hidden">
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-28 sm:h-48 bg-[#E8ECF5] rounded-lg mb-2 sm:mb-4 shadow-neomorph-inset flex items-center justify-center">
                    <Package className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                  </div>
                )}
                
                <div className="space-y-1.5 sm:space-y-3">
                  {/* Category Badge */}
                  {product.category && (
                    <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-50 text-gray-600 text-[8px] sm:text-xs rounded-lg border border-gray-200">
                      <span className="mr-0.5 sm:mr-1">{getCategoryIcon(product.category)}</span>
                      {getCategoryName(product.category)}
                    </span>
                  )}
                  
                  {/* Featured Badge */}
                  {product.featured && (
                    <span className="inline-block px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-50 text-yellow-700 text-[9px] sm:text-xs font-medium rounded-lg border border-yellow-200 shadow-neomorph-inset">
                      Featured
                    </span>
                  )}
                  
                  {/* Product Name */}
                  <h3 className="font-semibold text-gray-800 text-xs sm:text-sm line-clamp-2">{product.name}</h3>
                  
                  {/* Description - Hidden on very small screens */}
                  <p className="hidden sm:block text-xs text-gray-500 line-clamp-2 bg-[#E8ECF5] p-2 rounded-lg shadow-neomorph-inset">
                    {product.description}
                  </p>
                  
                  {/* Price and Rating */}
                  <div className="flex flex-wrap items-center justify-between gap-1">
                    <div className="flex items-baseline flex-wrap">
                      <span className="text-sm sm:text-lg font-bold text-primary-600">
                        {formatAmount(product.price)}
                      </span>
                      {product.compareAtPrice && (
                        <span className="ml-1 sm:ml-2 text-[9px] sm:text-xs text-gray-400 line-through">
                          {formatAmount(product.compareAtPrice)}
                        </span>
                      )}
                    </div>
                    {product.rating > 0 && (
                      <div className="flex items-center bg-yellow-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg shadow-neomorph-inset">
                        {renderStars(product.rating)}
                        <span className="ml-0.5 sm:ml-1 text-[9px] sm:text-xs text-gray-600">
                          ({product._count?.reviews || 0})
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Stock Status and Action */}
                  <div className="flex items-center justify-between gap-2 pt-1.5 sm:pt-2 border-t border-gray-100">
                    <span className={`text-[9px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg ${
                      product.quantity > 0 
                        ? 'bg-green-50 text-green-700 border border-green-200 shadow-neomorph-inset' 
                        : 'bg-red-50 text-red-700 border border-red-200 shadow-neomorph-inset'
                    }`}>
                      {product.quantity > 0 ? `${product.quantity} in stock` : 'Out of stock'}
                    </span>
                    <button
                      onClick={() => handleOrder(product)}
                      className={`rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-xs font-medium flex items-center transition-all ${
                        product.quantity > 0
                          ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30 hover:shadow-lg'
                          : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      disabled={product.quantity === 0}
                    >
                      <ShoppingCart className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1.5" />
                      <span className="hidden xs:inline">Add to Cart</span>
                      <span className="xs:hidden">Buy</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-6 sm:p-8 shadow-neomorph border border-gray-100 text-center">
            <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Package className="h-7 w-7 sm:h-10 sm:w-10 text-primary-600" />
            </div>
            <h3 className="text-sm sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">No products found</h3>
            <p className="text-xs sm:text-sm text-gray-500">
              {searchQuery || selectedCategory !== 'all' ? 'Try a different search or category' : 'This business has no products listed yet'}
            </p>
            {(searchQuery || selectedCategory !== 'all') && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="mt-3 sm:mt-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium shadow-md shadow-primary-500/30 hover:shadow-lg transition-all"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Order Modal */}
      {showOrderModal && selectedProduct && selectedBusiness && (
        <OrderModal
          item={selectedProduct}
          type="product"
          business={selectedBusiness}
          onClose={() => {
            setShowOrderModal(false);
            setSelectedProduct(null);
          }}
          onSuccess={(order) => {
            toast.success('Order placed successfully!');
            loadProducts(); // Refresh product list to update inventory
          }}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ProductListPage;